/**
 * V37.62 LEGALCOOP — Gestion financiere (Phase 1 : Devis).
 *
 * Espace admin pour gerer le cycle de vie des devis LEGALCOOP :
 *  - Creation d'un devis (avec/sans rattachement projet CCMI)
 *  - Generation PDF deterministe DEV-LC-YYYY-NNNNN
 *  - Envoi : marque sent + genere lien Stripe Checkout pre-rempli
 *  - Acceptation manuelle (signature recue)
 *  - Conversion en facture (cree une demande LEGALCOOP paid + facture PDF)
 *  - Annulation
 *
 * Reserve admin / super_admin uniquement.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Loader2, ArrowLeft, RefreshCw, Plus, FileText, Send, CheckCircle2, XCircle,
  Copy, FileDown, ArrowRightLeft, Link2, Receipt, RotateCcw, Download,
} from 'lucide-react';
import { LEGALCOOP_OFFICIAL_DEFINITION } from '../constants/legalcoop';
import { useLegalcoopFinance } from './useLegalcoopFinance';

const STATUS_META = {
  draft:     { label: 'Brouillon',  color: 'bg-slate-600 text-white border-slate-800 font-semibold' },
  sent:      { label: 'Envoyé',     color: 'bg-amber-600 text-white border-amber-800 font-semibold' },
  accepted:  { label: 'Accepté',    color: 'bg-blue-700 text-white border-blue-900 font-semibold' },
  invoiced:  { label: 'Facturé',    color: 'bg-emerald-700 text-white border-emerald-900 font-semibold' },
  expired:   { label: 'Expiré',     color: 'bg-orange-600 text-white border-orange-800 font-semibold' },
  cancelled: { label: 'Annulé',     color: 'bg-red-700 text-white border-red-900 font-semibold' },
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return iso.slice(0, 10);
  }
};

const fmtMoney = (v) => {
  if (v == null) return '-';
  return `${Number(v).toFixed(2).replace('.', ',')} €`;
};

export default function LegalcoopFinanceDashboard() {
  const navigate = useNavigate();
  const {
    loading, items, counters, filterStatus, setFilterStatus,
    createOpen, setCreateOpen, createEmail, setCreateEmail, createName, setCreateName,
    createCcmiRef, setCreateCcmiRef, createNotes, setCreateNotes, createValidity, setCreateValidity, createSubmitting,
    linkResult, setLinkResult, fetchList, submitCreate, handleDownloadPdf, handleSend, handleAccept, handleConvert, handleCancel, copyToClipboard,
    totalDevis, totalHt, fiscal, fiscalLoading, avoirs, recus, kpi, exportingCsv, downloadDocPdf, downloadCsv,
  } = useLegalcoopFinance();

  return (
    <div className="container mx-auto px-4 py-6 space-y-4" data-testid="legalcoop-finance-page">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/legalcoop')} className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour LEGALCOOP
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-indigo-700" />
            Gestion financière LEGALCOOP
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Devis, factures, reçus et avoirs du programme COPPAM / LEGALCOOP. Émis par OBJECTIF SCOP OUTREMER SCIC SAS.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setCreateOpen(true)} className="bg-indigo-700 hover:bg-indigo-800 text-white"
            data-testid="legalcoop-finance-create-devis-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouveau devis
          </Button>
          <Button onClick={downloadCsv} disabled={exportingCsv}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="legalcoop-finance-export-csv-btn">
            {exportingCsv ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
            Export CSV
          </Button>
          <Button variant="outline" onClick={fetchList} disabled={loading} data-testid="legalcoop-finance-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Actualiser
          </Button>
        </div>
      </div>

      {/* Definition officielle */}
      <Card className="border-indigo-200 bg-indigo-50">
        <CardContent className="pt-4 pb-3 text-xs text-slate-700 italic leading-relaxed">
          {LEGALCOOP_OFFICIAL_DEFINITION}
        </CardContent>
      </Card>

      {/* V37.63 — Connexion au registre fiscal Super Admin */}
      <Card className="border-violet-300 bg-gradient-to-br from-violet-50 to-indigo-50"
            data-testid="legalcoop-finance-registre-fiscal-card">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">⚖️</span>
              <h3 className="font-semibold text-violet-900">
                Connexion au registre fiscal Super Admin
              </h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/super-admin?tab=registre-fiscal')}
              className="border-violet-700 text-violet-900 hover:bg-violet-100"
              data-testid="legalcoop-finance-open-registre-btn">
              Ouvrir le registre fiscal
            </Button>
          </div>
          <p className="text-xs text-slate-700 mb-3">
            Toutes les demandes LEGALCOOP <b>payées</b>, <b>exonérées</b>, <b>assignées</b>, <b>en revue</b> ou <b>terminées</b>
            sont automatiquement agrégées dans le registre fiscal de OBJECTIF SCOP OUTREMER SCIC SAS,
            au même titre que les Plans PADH-, les Factures/Reçus ESS et les souscriptions DOS DOMIUM.
            La TVA collectée (DOM 8.5 %) est ventilée par mois.
          </p>
          {fiscalLoading ? (
            <div className="text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Calcul en cours…</div>
          ) : fiscal && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="bg-white rounded p-2 border border-violet-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Documents</div>
                <div className="text-lg font-bold text-violet-900" data-testid="fiscal-nb-docs">{fiscal.nb_documents}</div>
              </div>
              <div className="bg-white rounded p-2 border border-violet-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Dont exonérés</div>
                <div className="text-lg font-bold text-amber-700">{fiscal.nb_exempted}</div>
              </div>
              <div className="bg-white rounded p-2 border border-violet-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Total HT</div>
                <div className="text-lg font-bold text-slate-800" data-testid="fiscal-total-ht">{fmtMoney(fiscal.total_ht)}</div>
              </div>
              <div className="bg-white rounded p-2 border border-violet-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">TVA collectée</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="fiscal-total-tva">{fmtMoney(fiscal.total_tva)}</div>
              </div>
              <div className="bg-white rounded p-2 border border-violet-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Total TTC encaissé</div>
                <div className="text-lg font-bold text-indigo-700">{fmtMoney(fiscal.total_ttc)}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* V37.65 — KPI annuel CA / TVA / Avoirs / Net */}
      {kpi && (
        <Card className="border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50"
              data-testid="legalcoop-finance-kpi-annuel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              📊 KPI annuel {kpi.year}
              <span className="ml-auto text-xs font-normal text-slate-600">
                CA brut HT vs. avoirs vs. net comptable
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded p-3 border border-emerald-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">CA HT brut</div>
                <div className="text-xl font-bold text-emerald-900" data-testid="kpi-ca-ht">{fmtMoney(kpi.ca?.ht)}</div>
              </div>
              <div className="bg-white rounded p-3 border border-emerald-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">TVA collectée</div>
                <div className="text-xl font-bold text-blue-700" data-testid="kpi-tva">{fmtMoney(kpi.ca?.tva_collected)}</div>
              </div>
              <div className="bg-white rounded p-3 border border-emerald-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Avoirs émis ({kpi.avoirs?.count || 0})</div>
                <div className="text-xl font-bold text-red-700" data-testid="kpi-avoirs">-{fmtMoney(kpi.avoirs?.ht)}</div>
              </div>
              <div className="bg-white rounded p-3 border-2 border-emerald-700">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Net HT (comptable)</div>
                <div className="text-xl font-bold text-emerald-900" data-testid="kpi-net-ht">{fmtMoney(kpi.ca?.net_ht_after_avoirs)}</div>
              </div>
            </div>
            {kpi.monthly?.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-700">
                    <tr>
                      <th className="px-2 py-1 text-left">Mois</th>
                      <th className="px-2 py-1 text-right">Nb factures</th>
                      <th className="px-2 py-1 text-right">HT</th>
                      <th className="px-2 py-1 text-right">TVA</th>
                      <th className="px-2 py-1 text-right">TTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpi.monthly.map((m) => (
                      <tr key={m.mois} className="border-b border-slate-100" data-testid={`kpi-month-${m.mois}`}>
                        <td className="px-2 py-1 font-mono">{m.mois}</td>
                        <td className="px-2 py-1 text-right">{m.nb}</td>
                        <td className="px-2 py-1 text-right">{fmtMoney(m.ht)}</td>
                        <td className="px-2 py-1 text-right text-blue-700">{fmtMoney(m.tva)}</td>
                        <td className="px-2 py-1 text-right font-semibold">{fmtMoney(m.ttc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* V37.64 — Section Avoirs */}
      <Card data-testid="legalcoop-finance-avoirs-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-red-700" /> Avoirs émis ({avoirs.length})
            <span className="ml-auto text-xs font-normal text-amber-800 italic">
              ⚠ Sans remboursement — annulation comptable
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {avoirs.length === 0 ? (
            <div className="text-xs text-slate-500 italic">Aucun avoir émis pour l&apos;instant.</div>
          ) : (
            <div className="space-y-1" data-testid="legalcoop-finance-avoirs-list">
              {avoirs.slice(0, 8).map((a) => (
                <div key={a.id} className="flex items-center justify-between p-2 border border-red-200 rounded bg-red-50"
                     data-testid={`avoir-row-${a.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge className="bg-red-700 text-white border-red-900 font-semibold">{a.credit_number}</Badge>
                    <span className="text-slate-700">sur <b>{a.original_invoice_number}</b></span>
                    <span className="text-red-700 font-semibold">-{fmtMoney(a.amount_ht_credited)} HT</span>
                    <span className="text-xs text-slate-500 italic">{a.motif}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadDocPdf('avoir', a.id, a.credit_number)}
                    data-testid={`avoir-pdf-${a.id}`}>
                    <FileDown className="h-3 w-3 mr-1" /> PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* V37.64 — Section Reçus */}
      <Card data-testid="legalcoop-finance-recus-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="h-4 w-4 text-emerald-700" /> Reçus émis ({recus.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recus.length === 0 ? (
            <div className="text-xs text-slate-500 italic">Aucun reçu émis pour l&apos;instant.</div>
          ) : (
            <div className="space-y-1" data-testid="legalcoop-finance-recus-list">
              {recus.slice(0, 8).map((r) => (
                <div key={r.id} className="flex items-center justify-between p-2 border border-emerald-200 rounded bg-emerald-50"
                     data-testid={`recu-row-${r.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge className="bg-emerald-700 text-white border-emerald-900 font-semibold">{r.recu_number}</Badge>
                    {r.is_exoneration ? (
                      <Badge className="bg-purple-700 text-white border-purple-900 text-[10px] font-semibold">Exonération</Badge>
                    ) : (
                      <Badge className="bg-blue-700 text-white border-blue-900 text-[10px] font-semibold">{r.payment_mode}</Badge>
                    )}
                    <span className="text-slate-700">{r.client_email}</span>
                    <span className="text-slate-600">{fmtMoney(r.amount_ttc)}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadDocPdf('recu', r.id, r.recu_number)}
                    data-testid={`recu-pdf-${r.id}`}>
                    <FileDown className="h-3 w-3 mr-1" /> PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Devis */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="legalcoop-finance-kpi">
        {Object.entries(STATUS_META).map(([k, meta]) => {
          const c = counters[k] || { count: 0, total_ht: 0 };
          return (
            <Card key={k}>
              <CardContent className="pt-4">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{meta.label}</div>
                <div className="text-2xl font-bold">{c.count}</div>
                <div className="text-xs text-slate-600">{fmtMoney(c.total_ht)} HT</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-700 font-semibold">Filtre :</span>
            {['all', 'draft', 'sent', 'accepted', 'invoiced', 'expired', 'cancelled'].map((k) => (
              <Button
                key={k}
                variant={filterStatus === k ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(k)}
                data-testid={`legalcoop-finance-filter-${k}`}
              >
                {k === 'all' ? 'Tous' : STATUS_META[k]?.label || k}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Liste des devis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5" /> Devis ({totalDevis})
            <span className="ml-auto text-xs font-normal text-slate-600">Total HT affiché : {fmtMoney(totalHt)}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin inline" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate-500 italic">Aucun devis à afficher.</div>
          ) : (
            <div className="space-y-2" data-testid="legalcoop-finance-devis-list">
              {items.map((d) => {
                const meta = STATUS_META[d.status] || STATUS_META.draft;
                return (
                  <div key={d.id} className="p-3 border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                       data-testid={`devis-row-${d.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={meta.color}>{meta.label}</Badge>
                        <span className="font-mono text-sm font-semibold">{d.quote_number}</span>
                        <span className="text-sm text-slate-700">{fmtMoney(d.amount_ht)} HT</span>
                        <span className="text-xs text-slate-500">({fmtMoney(d.amount_ttc)} TTC)</span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(d)}
                          data-testid={`devis-pdf-${d.id}`}>
                          <FileDown className="h-3 w-3 mr-1" /> PDF
                        </Button>
                        {(d.status === 'draft' || d.status === 'sent') && (
                          <Button size="sm" variant="outline"
                            className="bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
                            onClick={() => handleSend(d)}
                            data-testid={`devis-send-${d.id}`}>
                            <Send className="h-3 w-3 mr-1" /> Envoyer / Lien Stripe
                          </Button>
                        )}
                        {(d.status === 'sent' || d.status === 'draft') && (
                          <Button size="sm" variant="outline"
                            className="bg-blue-50 text-blue-900 border-blue-300 hover:bg-blue-100"
                            onClick={() => handleAccept(d)}
                            data-testid={`devis-accept-${d.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Accepter
                          </Button>
                        )}
                        {d.status === 'accepted' && (
                          <Button size="sm"
                            className="bg-emerald-700 hover:bg-emerald-800 text-white"
                            onClick={() => handleConvert(d)}
                            data-testid={`devis-convert-${d.id}`}>
                            <ArrowRightLeft className="h-3 w-3 mr-1" /> Convertir en facture
                          </Button>
                        )}
                        {d.checkout_url && (
                          <Button size="sm" variant="outline"
                            onClick={() => copyToClipboard(d.checkout_url)}
                            data-testid={`devis-copy-link-${d.id}`}>
                            <Copy className="h-3 w-3 mr-1" /> Copier lien
                          </Button>
                        )}
                        {!['cancelled', 'invoiced'].includes(d.status) && (
                          <Button size="sm" variant="outline"
                            className="text-red-700 border-red-300 hover:bg-red-50"
                            onClick={() => handleCancel(d)}
                            data-testid={`devis-cancel-${d.id}`}>
                            <XCircle className="h-3 w-3 mr-1" /> Annuler
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-slate-600 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1">
                      <div><span className="text-slate-400">Client :</span> {d.client_email}</div>
                      {d.client_name && <div><span className="text-slate-400">Nom :</span> {d.client_name}</div>}
                      {d.ccmi_project_reference && <div><span className="text-slate-400">Réf. CCMI :</span> {d.ccmi_project_reference}</div>}
                      <div><span className="text-slate-400">Émis le :</span> {fmtDate(d.issued_at)}</div>
                      <div><span className="text-slate-400">Valable jusqu&apos;au :</span> {fmtDate(d.valid_until)}</div>
                      {d.linked_request_id && <div><span className="text-slate-400">Demande liée :</span> <span className="font-mono">{d.linked_request_id.slice(0, 8)}</span></div>}
                    </div>
                    {d.notes && (
                      <div className="mt-2 text-xs text-slate-600 italic border-l-2 border-slate-300 pl-2">{d.notes}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal creation */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="legalcoop-finance-create-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau devis LEGALCOOP</DialogTitle>
            <DialogDescription>
              Forfait Revue de conformité CCMI — 500 € HT. Émetteur : OBJECTIF SCOP OUTREMER SCIC SAS.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Email client *</label>
              <Input type="email" placeholder="client@example.fr" value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                data-testid="legalcoop-finance-create-email" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Nom / Raison sociale (optionnel)</label>
              <Input value={createName} onChange={(e) => setCreateName(e.target.value)}
                data-testid="legalcoop-finance-create-name" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Référence projet CCMI (optionnel)</label>
              <Input placeholder="CCMI-XXX-..." value={createCcmiRef}
                onChange={(e) => setCreateCcmiRef(e.target.value)}
                data-testid="legalcoop-finance-create-ccmiref" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Durée de validité (jours, 1–180)</label>
              <Input type="number" min={1} max={180} value={createValidity}
                onChange={(e) => setCreateValidity(e.target.value)}
                data-testid="legalcoop-finance-create-validity" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Notes internes (optionnel)</label>
              <Textarea rows={2} value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                data-testid="legalcoop-finance-create-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createSubmitting}>
              Annuler
            </Button>
            <Button onClick={submitCreate} disabled={createSubmitting}
              className="bg-indigo-700 hover:bg-indigo-800 text-white"
              data-testid="legalcoop-finance-create-submit">
              {createSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Créer le devis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal resultat lien Stripe apres send */}
      <Dialog open={!!linkResult} onOpenChange={(o) => { if (!o) setLinkResult(null); }}>
        <DialogContent data-testid="legalcoop-finance-link-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-indigo-700" /> Lien Stripe généré
            </DialogTitle>
            <DialogDescription>
              Le devis <b>{linkResult?.devis?.quote_number}</b> est marqué <b>envoyé</b>.
              Copiez le lien et envoyez-le au client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-slate-600">Lien Stripe Checkout (one-shot) :</label>
            <div className="flex gap-1">
              <Input readOnly value={linkResult?.checkout_url || ''}
                className="font-mono text-xs"
                onFocus={(e) => e.target.select()}
                data-testid="legalcoop-finance-link-url" />
              <Button variant="outline"
                onClick={() => copyToClipboard(linkResult?.checkout_url)}
                data-testid="legalcoop-finance-link-copy">
                <Copy className="h-4 w-4 mr-1" /> Copier
              </Button>
            </div>
            <p className="text-[11px] text-amber-800 italic">
              ⚠ Le lien expire selon la politique Stripe (24h par défaut).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkResult(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
